import { safeJsonParse } from "@/utils/safeJson";

type UnknownRecord = Record<string, unknown>;

export const BRIEFING_DEPARTAMENTAL_KEY = "briefing_departamental";

export function isBriefingRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === "object";
}

export function parseBriefingObject(value: unknown): UnknownRecord {
  if (isBriefingRecord(value)) return value;
  if (typeof value !== "string" || !value.trim()) return {};

  return safeJsonParse<UnknownRecord>(value, {});
}

export function normalizeBriefingArray<T = UnknownRecord>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (typeof value !== "string" || !value.trim()) return [];

  return safeJsonParse<T[]>(value, []);
}

export function stripBriefingHtml(value: unknown) {
  return String(value ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function hasBriefingContent(value: unknown) {
  const html = typeof value === "string" ? value : "";

  return (
    stripBriefingHtml(html).length > 0 ||
    /<(img|iframe|video|audio)\b/i.test(html)
  );
}

function parseLegacyBlockTitle(title: string): UnknownRecord {
  const parsed: UnknownRecord = {};

  title
    .split(/\s+\u2014\s+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const normalized = part
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase("pt-BR");
      const value = part.slice(part.indexOf(":") + 1).trim();

      if (normalized.startsWith("responsavel:")) {
        parsed.responsavel_nome = value;
        return;
      }

      if (normalized.startsWith("status:")) {
        parsed.status_nome = value;
        return;
      }

      if (!parsed.departamento_nome) parsed.departamento_nome = part;
    });

  return parsed;
}

function getFirstDivHtml(sectionHtml: string) {
  const divMatch = sectionHtml.match(/<div[^>]*>([\s\S]*)<\/div>/i);

  return divMatch?.[1]?.trim() ?? "";
}

function getLegacyBriefingBlocksWithRegex(html: string): UnknownRecord[] {
  return Array.from(html.matchAll(/<section[^>]*>([\s\S]*?)<\/section>/gi))
    .map((match) => {
      const sectionHtml = match[1] ?? "";
      const title = stripBriefingHtml(
        sectionHtml.match(/<h3[^>]*>([\s\S]*?)<\/h3>/i)?.[1] ?? "",
      );
      const briefing = getFirstDivHtml(
        sectionHtml.replace(/<h3[^>]*>[\s\S]*?<\/h3>/i, ""),
      );

      return {
        ...parseLegacyBlockTitle(title),
        briefing,
      };
    })
    .filter((block) => hasBriefingContent(block.briefing));
}

export function getLegacyBriefingDepartamentalBlocks(
  objetivo: unknown,
): UnknownRecord[] {
  const html = typeof objetivo === "string" ? objetivo : "";
  if (!html.trim() || !/<section/i.test(html)) return [];

  if (typeof DOMParser !== "undefined") {
    try {
      const document = new DOMParser().parseFromString(html, "text/html");
      const sections = Array.from(document.querySelectorAll("section"));
      const blocks = sections
        .map((section) => {
          const title = section.querySelector("h3")?.textContent ?? "";
          const briefing = section.querySelector("div")?.innerHTML ?? "";

          return {
            ...parseLegacyBlockTitle(title),
            briefing,
          };
        })
        .filter((block) => hasBriefingContent(block.briefing));

      if (blocks.length > 0) return blocks;
    } catch {
      return getLegacyBriefingBlocksWithRegex(html);
    }
  }

  return getLegacyBriefingBlocksWithRegex(html);
}

export function getBriefingDepartamentalBlocks(data: unknown): UnknownRecord[] {
  if (!isBriefingRecord(data)) return [];

  const customFields = parseBriefingObject(
    data.customFields ?? data.custom_fields,
  );
  const briefing = parseBriefingObject(customFields[BRIEFING_DEPARTAMENTAL_KEY]);
  const blocks = normalizeBriefingArray<UnknownRecord>(briefing.blocks).filter(
    (block) => hasBriefingContent(block.briefing),
  );

  if (briefing.mode === "departamental" && blocks.length > 0) return blocks;

  return getLegacyBriefingDepartamentalBlocks(data.objetivo);
}
